'use client';

import { useState, useEffect, useRef } from 'react';

interface UseHeaderCollapseOptions {
  threshold?: number;
  debounceMs?: number;
}

interface UseHeaderCollapseReturn {
  isCollapsed: boolean;
  scrollY: number;
}

export const useHeaderCollapse = ({
  threshold = 100,
  debounceMs = 16,
}: UseHeaderCollapseOptions = {}): UseHeaderCollapseReturn => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const timeoutRef = useRef<NodeJS.Timeout>(undefined);

  useEffect(() => {
    const handleScroll = (e: Event) => {
      const target = e.target as HTMLElement;

      // Only respond to scroll events from overflow-y-auto elements
      if (!target?.classList?.contains('overflow-y-auto')) return;

      // Ignore scroll events from dropdown portals
      const isInDropdown = target?.closest(
        '[data-testid="volume-selector-dropdown"]'
      );
      if (isInDropdown) return;

      // Ignore scroll events from elements with data-no-scroll
      if (
        target?.hasAttribute('data-no-scroll') ||
        target?.closest('[data-no-scroll]')
      )
        return;

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        const currentScrollY = target.scrollTop;
        setScrollY(currentScrollY);
        setIsCollapsed(currentScrollY > threshold);
      }, debounceMs);
    };

    document.addEventListener('scroll', handleScroll, {
      passive: true,
      capture: true,
    });

    return () => {
      document.removeEventListener('scroll', handleScroll, { capture: true });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [threshold, debounceMs]);

  return {
    isCollapsed,
    scrollY,
  };
};

export default useHeaderCollapse;
